import React from 'react';
import { handleChunkLoadError, isChunkLoadError } from '../../utils/chunkLoadErrorHandler';

/**
 * Error Boundary for Chunk Loading Errors
 * Automatically reloads the page when chunk loading fails
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Try to handle chunk load error
    if (isChunkLoadError(error)) {
      handleChunkLoadError(error);
    }
  }

  render() {
    if (this.state.hasError) {
      // If it's a chunk load error, show a loading state while reloading
      if (isChunkLoadError(this.state.error)) {
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f9fafb',
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
            }}>
              🔄
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '10px',
              color: '#1f2937',
            }}>
              Loading Application Update
            </h2>
            <p style={{
              color: '#6b7280',
              maxWidth: '400px',
            }}>
              We're loading the latest version of the application. This will only take a moment...
            </p>
            <div style={{
              marginTop: '20px',
              display: 'flex',
              gap: '8px',
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                animation: 'bounce 1.4s infinite ease-in-out both',
                animationDelay: '-0.32s',
              }} />
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                animation: 'bounce 1.4s infinite ease-in-out both',
                animationDelay: '-0.16s',
              }} />
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                animation: 'bounce 1.4s infinite ease-in-out both',
              }} />
            </div>
            <style>
              {`
                @keyframes bounce {
                  0%, 80%, 100% {
                    transform: scale(0);
                  }
                  40% {
                    transform: scale(1);
                  }
                }
              `}
            </style>
          </div>
        );
      }

      // For other errors, show a generic error message with retry option
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
          }}>
            ⚠️
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '10px',
            color: '#1f2937',
          }}>
            Something Went Wrong
          </h2>
          <p style={{
            color: '#6b7280',
            maxWidth: '400px',
            marginBottom: '20px',
          }}>
            We encountered an unexpected error. Please try refreshing the page.
          </p>
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
            }}
          >
            Refresh Page
          </button>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{
              marginTop: '20px',
              textAlign: 'left',
              maxWidth: '600px',
              width: '100%',
              backgroundColor: 'white',
              padding: '15px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
            }}>
              <summary style={{
                cursor: 'pointer',
                fontWeight: '600',
                color: '#1f2937',
              }}>
                Error Details (Development Only)
              </summary>
              <pre style={{
                marginTop: '10px',
                fontSize: '12px',
                overflow: 'auto',
                color: '#ef4444',
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
